import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ButtonDS,
  CritAir,
  EnergyClass,
  MessageTooltip,
  NotInterestedOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  TableTagVehicleConnectionStatus,
  TableTagVehicleStatus,
  Tag,
  TextCapitalized,
  WifiTetheringOutlined,
  dateFormatterDayMonthYear,
} from "@qivia/ui";
import { useAppSelector } from "../../../../redux/hooks";
import { selectListAllVehiclesInDb } from "../vehiclesSlice";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LittleCardAsset } from "@qivia/ui/src/assets/assets";
import { DeactivateVehicleModal } from "../modals/deactivateVehicleModal";
import { AddSingleVehicleRequestConnectionModal } from "../modals/addSingleVehicleRequestConnectionModal";
import "../../../../i18n/i18n";
import { AnalyticCodeSection } from "./analyticCodeSection";
import { DepartmentSection } from "./departmentSection";
import { NoteTakingCodeSection } from "./noteTakingCodeSection";
import { UpdateTypeSection } from "./updateTypeSection";
import { DriverHistorySection } from "./driverHistorySection";
import {
  VehicleConnectionStatus,
  VehicleStatus,
} from "@qivia/schemas-cards-vehicles";
import { formatDateTime } from "../../../../libSlice/materialUiFormats";

export interface VehicleSidePanelProps {
  canUpdateDepartment?: boolean;
  canUpdateAnalyticCode?: boolean;
  canUpdateNoteTaking?: boolean;
  canUpdateType?: boolean;
  canDeactivate?: boolean;
  canRequestConnection?: boolean;
  hasAdvancedAnalytics?: boolean;
}

export const VehicleSidePanel: FunctionComponent<VehicleSidePanelProps> = (
  props: VehicleSidePanelProps,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();

  const listAllVehiclesInDb = useAppSelector(selectListAllVehiclesInDb);
  const vehicle = listAllVehiclesInDb.find((v) => v.uuid === params.id);
  const [isDeactivateModalVisible, setIsDeactivateModalVisible] =
    useState(false);
  const [isRequestConnectionModalVisible, setIsRequestConnectionModalVisible] =
    useState(false);
  const firstPath = location.pathname.split("/")[1]; // home or departments

  useEffect(() => {
    if (!vehicle) {
      navigate(`/${firstPath}/vehicles`);
    }
  }, [firstPath, navigate, vehicle]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/vehicles`);
  }, [firstPath, navigate]);

  const connectionStatus = vehicle?.connectionStatus;

  const isConnectionError = useMemo(
    () => !connectionStatus,
    [connectionStatus],
  );

  const canRequestConnection = useMemo(
    () =>
      !!props.canRequestConnection &&
      (isConnectionError ||
        connectionStatus === "IDLE" ||
        connectionStatus === "REVOKED"),
    [props.canRequestConnection, isConnectionError, connectionStatus],
  );

  if (!vehicle) {
    return;
  }

  return (
    <SidePanelDS.SidePanel
      visible={true}
      onClose={onSidePanelClosed}
      absoluteSection={
        canRequestConnection || props.canDeactivate ? (
          <>
            {canRequestConnection && (
              <ButtonDS
                leftIcon={<WifiTetheringOutlined />}
                text={t("vehicles.sidepanel.requestConnection")}
                format={"fill"}
                buttonType={"primary"}
                onClick={() =>
                  !isConnectionError && setIsRequestConnectionModalVisible(true)
                }
                disabled={isConnectionError}
              />
            )}
            {canRequestConnection && props.canDeactivate && <Spacer y={1} />}
            {props.canDeactivate && (
              <ButtonDS
                leftIcon={<NotInterestedOutlined />}
                text={t("vehicles.sidepanel.deactivate")}
                format={"fill"}
                buttonType={"secondary"}
                onClick={() =>
                  vehicle.status !== "DEACTIVATED" &&
                  setIsDeactivateModalVisible(!isDeactivateModalVisible)
                }
                iconColor={"errorNormal"}
                disabled={vehicle.status === "DEACTIVATED"}
              />
            )}
          </>
        ) : undefined
      }
    >
      <DeactivateVehicleModal
        isVisible={isDeactivateModalVisible}
        onCloseModal={() => {
          setIsDeactivateModalVisible(false);
        }}
        vehicleUuid={vehicle.uuid}
      />
      <AddSingleVehicleRequestConnectionModal
        visible={isRequestConnectionModalVisible}
        onCloseModal={() => {
          setIsRequestConnectionModalVisible(false);
        }}
        vehicleUuid={vehicle.uuid}
      />
      <SidePanelDS.Section
        verticalSpacing={1.5}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          <StyledContent>{vehicle.registrationNumber}</StyledContent>
          {vehicle.reference && (
            <>
              <Spacer y={0.5} />
              <Tag
                text={vehicle.reference}
                borderColor={colors["colors/borders/cells/cells"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                textColor={colors["colors/button/primary/default"]}
              />
            </>
          )}
          <StyledImg src={Car} alt="Car" />
          <Spacer y={1} />
          <StyledContent>
            {`${vehicle.brand ? vehicle.brand : ""} ${vehicle.model ? vehicle.model : ""}`.trim()}
          </StyledContent>
          {vehicle.energy && (
            <>
              <Spacer y={0.25} />
              <StyledEnergie>
                <TextCapitalized>
                  {vehicle.energy && t(`vehicles.energie.${vehicle.energy}`)}
                </TextCapitalized>
              </StyledEnergie>
            </>
          )}
        </StyledPanelContainer>
        <Spacer y={1.5} />
        {vehicle.cardUuid && vehicle.rule && (
          <SidePanelDS.ActionBlock>
            <>
              {vehicle.cardUuid && vehicle.cardName && (
                <SidePanelDS.SectionedActionStripe
                  left={
                    <StyledRow>
                      <LittleCardAsset />
                      <Spacer x={1} />
                      {`**** ${vehicle.cardName}`}
                    </StyledRow>
                  }
                  right={
                    <StyledLittleIcon>
                      <RemoveRedEyeOutlined
                        key="card"
                        onClick={() =>
                          navigate(`/home/cards/${vehicle.cardUuid}`)
                        }
                      />
                      <Spacer x={1} />
                    </StyledLittleIcon>
                  }
                />
              )}
              {vehicle.rule && (
                <SidePanelDS.SectionedActionStripe
                  left={
                    <StyledRowCenter>
                      <StyledBodyM>
                        <TextCapitalized>
                          {t(`cards.sidePanel.informations.title.rule`)}
                        </TextCapitalized>
                      </StyledBodyM>
                      <Spacer x={0.5} />
                      {vehicle.ruleName}
                    </StyledRowCenter>
                  }
                  right={
                    <StyledLittleIcon>
                      <RemoveRedEyeOutlined
                        key="rule"
                        onClick={() => navigate(`/home/rule/${vehicle.rule}`)}
                      />
                      <Spacer x={1} />
                    </StyledLittleIcon>
                  }
                />
              )}
            </>
          </SidePanelDS.ActionBlock>
        )}
      </SidePanelDS.Section>
      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.details")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.status")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <TableTagVehicleStatus
              status={t(`vehicles.status.${vehicle.status}`) as VehicleStatus}
            />
          }
        />

        {props.hasAdvancedAnalytics && (
          <>
            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <SectionRowLabel>
                  <TextCapitalized>
                    {t("vehicles.sidepanel.connectionStatus")}
                  </TextCapitalized>
                </SectionRowLabel>
              }
              value={
                vehicle.connectionStatus ? (
                  <TableTagVehicleConnectionStatus
                    connectionStatus={
                      t(
                        `vehicles.connectionStatus.${vehicle.connectionStatus}`,
                      ) as VehicleConnectionStatus
                    }
                  />
                ) : (
                  <>
                    <TextCapitalized>-</TextCapitalized>
                    <Spacer x={0.5} />
                  </>
                )
              }
            />

            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <SectionRowLabel>
                  <TextCapitalized>
                    {t("vehicles.sidepanel.lastTelemetryDataDate")}
                  </TextCapitalized>
                </SectionRowLabel>
              }
              value={
                <>
                  <TextCapitalized>
                    {vehicle.lastTelemetryDataDate
                      ? formatDateTime(vehicle.lastTelemetryDataDate)
                      : "-"}
                  </TextCapitalized>
                  <Spacer x={0.5} />
                </>
              }
            />

            {vehicle.connectionStatus === "PENDING" && (
              <>
                <Spacer y={2} />
                <MessageTooltip
                  title={t("vehicles.sidepanel.requestedConnectionAt.title", {
                    date: vehicle.requestedConnectionAt
                      ? dateFormatterDayMonthYear(
                          new Date(vehicle.requestedConnectionAt),
                        )
                      : "-",
                  })}
                  text={t("vehicles.sidepanel.requestedConnectionAt.text")}
                  type={"NEUTRAL"}
                />
              </>
            )}

            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <SectionRowLabel>
                  <TextCapitalized>
                    {t("vehicles.sidepanel.dataProvider")}
                  </TextCapitalized>
                </SectionRowLabel>
              }
              value={
                <>
                  <TextCapitalized>
                    {vehicle.dataProvider
                      ? t(`vehicles.dataProvider.${vehicle.dataProvider}`)
                      : "-"}
                  </TextCapitalized>
                  <Spacer x={0.5} />
                </>
              }
            />
          </>
        )}

        {props.canUpdateType && (
          <>
            <Spacer y={2} />
            <UpdateTypeSection vehicle={vehicle} />
          </>
        )}

        {props.canUpdateNoteTaking && (
          <>
            <Spacer y={2} />
            <NoteTakingCodeSection
              noteTaking={vehicle.noteTaking}
              vehicleUuid={vehicle.uuid}
              vehicleUpdatedAt={vehicle.updatedAt}
            />
          </>
        )}
      </SidePanelDS.Section>

      {props.canUpdateAnalyticCode ? (
        <AnalyticCodeSection
          analyticCode={vehicle.analyticCode}
          vehicleUuid={vehicle.uuid}
        />
      ) : (
        <></>
      )}

      {props.canUpdateDepartment ? (
        <DepartmentSection
          department={vehicle.department}
          vehicleUuid={vehicle.uuid}
          vehiclesList={listAllVehiclesInDb}
        />
      ) : (
        <></>
      )}
      <DriverHistorySection />

      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.informations")}
          </TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.mileage")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.latestMileage ? "-" : vehicle.latestMileage.toFixed(2)}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.mileage.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.latestMileageDate && (
          <StyledSubTitle>
            <TextCapitalized>
              {t("vehicles.sidepanel.mileageDate")}
            </TextCapitalized>
            <Spacer x={0.25} />:<Spacer x={0.25} />
            {dateFormatterDayMonthYear(new Date(vehicle.latestMileageDate))}
          </StyledSubTitle>
        )}
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.consumption.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.averageConsumption ? "-" : vehicle.averageConsumption}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.consumption.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <StyledSubTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.consumption.subTitle")}
          </TextCapitalized>
        </StyledSubTitle>
        {props.hasAdvancedAnalytics && vehicle.deviceLastActivityDate && (
          <>
            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <SectionRowLabel>
                  <TextCapitalized>
                    {t("vehicles.sidepanel.deviceLastActivityDate")}
                  </TextCapitalized>
                </SectionRowLabel>
              }
              value={
                <>
                  <TextCapitalized>
                    {dateFormatterDayMonthYear(
                      new Date(vehicle.deviceLastActivityDate),
                    )}
                  </TextCapitalized>
                  <Spacer x={0.5} />
                </>
              }
            />
          </>
        )}
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.pollution")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.emission")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.averageCarbonEmissionPerKm === null
                ? "-"
                : vehicle.averageCarbonEmissionPerKm}
              <Spacer x={0.25} />
              <StyledSubTitle>
                {t("vehicles.sidepanel.emission.units")}
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.energyClass.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.energyClass === null ? (
                "-"
              ) : (
                <>
                  <EnergyClass
                    energyClass={vehicle.energyClass as EnergyClass}
                  />
                  <Spacer x={0.25} />
                </>
              )}
              <Spacer x={0.25} />
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.energyClass && (
          <StyledSubTitle>
            <TextCapitalized>
              {t(`vehicles.sidepanel.energyClass.${vehicle.energyClass}`)}
            </TextCapitalized>
            <Spacer x={0.25} />
          </StyledSubTitle>
        )}
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.critAir")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.critAir === null ? (
                <>
                  {"-"} <Spacer x={0.25} />
                </>
              ) : (
                <CritAir critAir={`${vehicle.critAir}`.toString() as CritAir} />
              )}
            </StyledRow>
          }
        />
      </SidePanelDS.Section>
    </SidePanelDS.SidePanel>
  );
};

const StyledSubTitle = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledBodyM = styled.div`
  ${typographies["Body/M"]}
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledImg = styled.img`
  filter: grayscale(100%);
  width: 15rem;
  height: 6.5rem;
  object-fit: contain;
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledEnergie = styled.span`
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
  text-align: center;
`;
